/**
 * Design and Engineering Page - WoodAlps Manufacturing Excellence
 * 
 * This comprehensive page showcases WoodAlps' design philosophy and engineering process:
 * - Manufacturing process documentation with visuals
 * - Technical specifications and material information
 * - Design philosophy and craftsmanship details
 * - Engineering standards and quality control
 * - Interactive content sections with smooth scrolling
 * - Social media integration for behind-the-scenes content
 * 
 * Technical Features:
 * - Dynamic header height calculations for responsive design
 * - Smooth scrolling navigation between sections
 * - Portable text rendering for rich CMS content
 * - Optimized images for manufacturing process visuals
 * - Custom typography with brand font integration
 * 
 * Content Sections:
 * - Design philosophy and approach
 * - Manufacturing process walkthrough
 * - Quality control and testing procedures
 * - Material selection and sustainability
 * - Technical specifications and standards
 * 
 * @description Manufacturing and design process showcase with technical details
 */

import Head from 'next/head';
import Image from 'next/image';
import { ChevronDown, Instagram, Facebook } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useRef, useState, useEffect } from 'react';
import { getDesignEngineeringPageData, } from '../src/app/studio/lib/sanity';
import { urlForImage } from '../src/app/studio/lib/image';
import { sanityLoader } from '../utils/sanityLoader';
import { PortableText } from '@portabletext/react';
import { useTranslation } from '../src/hooks/useTranslation';

// Custom portable text components for rich content rendering
const portableTextComponents = {
  marks: {
    neuropol: ({children}) => <span className="font-neuropol">{children}</span>,
    montserrat: ({children}) => <span className="font-montserrat">{children}</span>,
    strong: ({children}) => <strong>{children}</strong>,
    em: ({children}) => <em>{children}</em>,
  },
  block: {
    normal: ({children}) => <p className="mb-4 last:mb-0">{children}</p>,
  },
};

// Helper function to render portable text or fallback to string
const renderPortableText = (portableText, fallbackText) => {
  // Check if portableText exists, is an array, has content, and that content has text
  if (portableText && 
      Array.isArray(portableText) && 
      portableText.length > 0 && 
      portableText.some(block => 
        block.children && 
        block.children.length > 0 && 
        block.children.some(child => child.text && child.text.trim().length > 0)
      )
  ) {
    return <PortableText value={portableText} components={portableTextComponents} />;
  }
  // Return fallback text if any of the above conditions aren't met
  return <p>{fallbackText}</p>;
};

export default function DesignAndEngineering({ pageData }) {
  const contentRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const { t } = useTranslation();
  // Get header height on mount and window resize
  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector('header');
      if (header) {
        // Get the full height including navigation
        setHeaderHeight(header.offsetHeight + 40); // Adding 40px for the navigation bar below
      }
    };

    // Initial calculation
    updateHeaderHeight();
    
    // Update on resize
    window.addEventListener('resize', updateHeaderHeight);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  // Custom smooth scroll function with consistent animation
  const smoothScrollTo = (targetPosition, duration = 500) => {
    const startPosition = window.scrollY;
    const distance = targetPosition - startPosition;
    let startTime = null;

    function animation(currentTime) {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      // Easing function - ease-in-out cubic
      const ease = t => t<.5 ? 4*t*t*t : (t-1)*(2*t-2)*(2*t-2)+1;
      
      window.scrollTo(0, startPosition + distance * ease(progress));
      
      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    }
    
    requestAnimationFrame(animation);
  };

  const scrollToContent = () => {
    if (contentRef.current) {
      const sectionPosition = contentRef.current.getBoundingClientRect().top + window.scrollY;
      
      // Use exact headerHeight without additional offset
      smoothScrollTo(sectionPosition - headerHeight);
    }
  };

  // Render content sections from Sanity data
  const renderContentSections = () => {
    // This function is no longer used as we're directly rendering each section in the JSX
    return null;
  };

  // Render table section from Sanity data
  const renderTableSection = () => {
    // This function is no longer used as we're directly rendering the table section in the JSX
    return null;
  };

  return (
    <>
      <Head>
        <title>{pageData?.title || 'Design & Engineering | WoodAlps'}</title>
        <meta
          name="description"
          content={pageData?.description || 'Explore the engineering principles, materials, and craftsmanship behind WoodAlps wooden bicycle frames.'}
        />
        <style>{`
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {
              transform: translateY(0);
            }
            40% {
              transform: translateY(-20px);
            }
            60% {
              transform: translateY(-10px);
            }
          }
        `}</style>
      </Head>

      {/* Hero Banner */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src={pageData?.heroSection?.backgroundImage ? urlForImage(pageData.heroSection.backgroundImage) : "/images/d&e_heroFrame.png"}
            alt={pageData?.heroSection?.backgroundImage?.alt || "Design and Engineering"}
            fill
            loader={sanityLoader}
            sizes="100vw"
            className="object-cover"
            priority
          />
        </div>
        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 "></div>

        {/* Content */}
        <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 font-neuropol">
            {pageData?.heroSection?.title || 'Design & Engineering'}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl font-neuropol">
            {pageData?.heroSection?.subtitle || 'Creating the very best wooden frame is at the very heart of WOODALPS.'}
          </p>
          
          {/* Animated Down Arrow */}
          <button 
            onClick={scrollToContent} 
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white cursor-pointer focus:outline-none"
            aria-label="Scroll down"
          >
            <p className="text-lg mb-2 text-white">Explore more</p>
            <ChevronDown 
              size={32}
              className="mx-auto animate-bounce text-white" 
              style={{ 
                animation: 'bounce 1s infinite',
                opacity: 0.8,
              }} 
            />
          </button>
        </div>
      </section>

      {/* Main Content */}
      <div ref={contentRef} className="bg-white">
        {/* Section 1: Why Wood */}
        <section className="py-16 md:py-24">
          <div className="container-custom px-4 sm:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 font-neuropol">{pageData?.globalSection?.section1?.title || "Why Wood, and Which One?"}</h2>
                <div className="w-16 h-1 bg-accent-color mb-6 mx-auto"></div>
                <div className="mb-4">
                  {renderPortableText(pageData?.globalSection?.section1?.description, "At WOODALPS, we've chosen wood for our bike frames — and that's no accident. Most of our frames are made from ash wood, one of the toughest local hardwoods in Europe and America. Ash has a rich history of being used in demanding applications: it was used for airplane structures in the early days of aviation, in boat building, and it's still used today in the construction of wooden skis. Today, we've given it a new mission: to be the centerpiece of high-performance, elegant, and durable wooden bike frames.")}
                </div>
                <h3 className="text-xl font-bold mt-8 mb-4 font-neuropol">{pageData?.globalSection?.section1?.subtitle || "An Impressive Strength-to-Weight Ratio"}</h3>
                <div className="mb-4">
                  {renderPortableText(pageData?.globalSection?.section1?.description2, "Ash offers an excellent strength-to-weight ratio (18 MPa/kg), slightly lower than that of aluminum (25.5 MPa/kg). However, its low density, four times lower than aluminum, allows us to create larger and thicker profiles with increased flexural inertia.")}
                </div>
                <div>
                  {renderPortableText(pageData?.globalSection?.section1?.description3, "The result? Frames that are lightweight, stiff, and strong, while also giving us the freedom to craft bold shapes and unique design.")}
                </div>
              </div>
              <div className="order-1 md:order-2 grid grid-cols-1 gap-4">
                <div className="relative h-64 md:h-80 rounded-lg overflow-hidden">
                  <Image
                    src={pageData?.globalSection?.section1?.imageTop ? urlForImage(pageData.globalSection.section1.imageTop) : "/images/D&E_WhyWood_The-Ash-Tree.jpg"}
                    alt="Wooden skis made from ash wood"
                    fill
                    loader={sanityLoader}
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="object-cover"
                  />
                </div>
                <div className="relative h-64 md:h-80 rounded-lg overflow-hidden">
                  <Image
                    src={pageData?.globalSection?.section1?.imageBottom ? urlForImage(pageData.globalSection.section1.imageBottom) : "/images/D&E_WhyWood_Ash-Wood.jpg"}
                    alt="Boat crafted from ash wood"
                    fill
                    loader={sanityLoader}
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Weight */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container-custom px-4 sm:px-6">
            <div className="flex justify-center">
              <div className="max-w-3xl w-full">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 font-neuropol text-center">{pageData?.globalSection?.section2?.tableSection?.title || "How Much Does It Weigh?"}</h2>
                <div className="w-16 h-1 bg-accent-color mb-6 mx-auto"></div>
                
                {pageData?.globalSection?.section2?.tableSection?.description && (
                  <p className="text-center mb-6">{pageData.globalSection.section2.tableSection.description}</p>
                )}
                
                <div className="w-full -mx-4 px-4 sm:mx-0 sm:px-0">
                  <div className="overflow-x-auto hide-scrollbar">
                    <table className="min-w-[600px] sm:w-full bg-white rounded-lg overflow-hidden shadow-lg mb-6">
                      <thead className="bg-gray-800 text-white">
                        <tr>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 md:px-7 text-left font-neuropol text-xs sm:text-sm">Frame Type</th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 md:px-7 text-left font-neuropol text-xs sm:text-sm"> XS </th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 md:px-7 text-center font-neuropol text-xs sm:text-sm"> S </th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 md:px-7 text-center font-neuropol text-xs sm:text-sm"> M </th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 md:px-7 text-center font-neuropol text-xs sm:text-sm"> L </th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 md:px-7 text-center font-neuropol text-xs sm:text-sm"> XL </th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 md:px-7 text-center font-neuropol text-xs sm:text-sm"> XXL </th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 md:px-7 text-center font-neuropol text-xs sm:text-sm">Total Weight</th>
                        </tr>
                      </thead>
                    <tbody className="divide-y divide-gray-200">
                      {/* Use Sanity data if available, fallback to default data if not */}
                      {pageData?.globalSection?.section2?.tableSection?.tableRows?.length > 0 ? (
                        // Render rows from Sanity data
                        pageData.globalSection.section2.tableSection.tableRows.map((row, index) => (
                          <tr key={index}>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-bold font-neuropol text-xs sm:text-sm">{row.frameType}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">{row.sizeXS}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">{row.sizeS}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">{row.sizeM}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">{row.sizeL}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">{row.sizeXL}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">{row.sizeXXL}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">{row.totalWeight}</td>
                          </tr>
                        ))
                      ) : (
                        // Fallback data when no Sanity rows exist
                        <>
                          <tr>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-bold font-neuropol text-xs sm:text-sm">Road</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm"></td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">1.7</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">1.8</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">1.9</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">2.0</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm"></td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">≈ 7.5</td>
                          </tr>
                          <tr>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-bold font-neuropol text-xs sm:text-sm">MTB</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm"></td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">2.4</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">2.5</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">2.6</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">2.8</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm"></td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">≈ 10.2</td>
                          </tr>
                          <tr>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-bold font-neuropol text-xs sm:text-sm">Gravel</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm"></td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">2.0</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">2.1</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">2.2</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">2.3</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm"></td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">≈ 8.9</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                    </table>
                  </div>
                </div>
                <p className="text-center">
                  Light and powerful, a <span className="font-neuropol">WOODALPS</span> frame will carry you effortlessly <br/> on both asphalt and the toughest trails.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Strength */}
        <section className="py-16 md:py-24">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 font-neuropol">{pageData?.globalSection?.section3?.title || "Strength Built for Riding, Not Just for Show"}</h2>
                <div className="w-16 h-1 bg-accent-color mb-6 mx-auto"></div>
                <div className="mb-4">
                  {renderPortableText(pageData?.globalSection?.section3?.description1, "WOODALPS frames aren't meant to hang on the wall above your couch (although that's tempting). They're designed for action: whether you're on a casual ride, racing on the road, or tackling rough trails, our frames are made to last.")}
                </div>
                <div className="mb-4">
                  {renderPortableText(pageData?.globalSection?.section3?.description2, "Every model undergoes a series of rigorous tests, first on our in-house test bench, then at an accredited lab to meet the requirements of ISO 4210-2 — which includes fatigue and impact testing.")}
                </div>
                <div>
                  {renderPortableText(pageData?.globalSection?.section3?.description3, "Here are some examples of our tests:\n- Impact Test: simulating a frame drop.\n- Fatigue Tests: with vertical, horizontal, and pedaling forces.")}
                </div>
              </div>
              <div className="relative h-[400px] md:h-[500px] rounded-lg overflow-hidden">
                <Image
                  src={pageData?.globalSection?.section3?.image ? urlForImage(pageData.globalSection.section3.image) : "/images/D&E_WhyWood_Leafs.jpg"}
                  alt="Frame strength testing"
                  fill
                  loader={sanityLoader}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Design and Construction */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="relative h-[400px] md:h-[500px] rounded-lg overflow-hidden">
                <Image
                  src={pageData?.globalSection?.section4?.image ? urlForImage(pageData.globalSection.section4.image) : "/images/woodTubes.jpg"}
                  alt="Frame construction process"
                  fill
                  loader={sanityLoader}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="object-cover"
                />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 font-neuropol">{pageData?.globalSection?.section4?.title || "Design and Construction"}</h2>
                <div className="w-16 h-1 bg-accent-color mb-6"></div>
                <div className="mb-4">
                  {renderPortableText(pageData?.globalSection?.section4?.description1, "Each frame is made up of at least 10 wooden pieces, precisely machined using a CNC (computer numerical control) machine.")}
                </div>
                <div className="mb-6">
                  {renderPortableText(pageData?.globalSection?.section4?.description2, "The hollow tubes that form the frame have thicknesses optimized for local stress points and shock resistance. This clever design also allows for internal cable routing, giving the frame a sleek and streamlined appearance.")}
                </div>
                
                <h3 className="text-xl font-bold mt-8 mb-4 font-neuropol">{pageData?.globalSection?.section4?.subtitle || "Carbon Inserts and Assembly"}</h3>
                <div className="mb-4">
                  {renderPortableText(pageData?.globalSection?.section4?.description3, "To ensure maximum durability, we reinforce key interfaces—such as the headset, bottom bracket, and seat post with carbon inserts.")}
                </div>
                <div>
                  {renderPortableText(pageData?.globalSection?.section4?.description4, "The wooden parts are assembled with a high-strength epoxy adhesive and protected by an extremely durable UV-resistant varnish, which shields the frame from weather and wear.")}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: Durability */}
        <section className="py-16 md:py-24">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 font-neuropol">{pageData?.globalSection?.section5?.title || "Durability\nA Frame for Life - \nand Beyond"}</h2>
                <div className="w-16 h-1 bg-accent-color mb-6"></div>
                <div>
                  {renderPortableText(pageData?.globalSection?.section5?.description1, "Unlike some composite frames, WOODALPS frames show no signs of premature aging. After 10 years of use, our earliest models remain as strong and beautiful as ever. In other words, with minimal maintenance, your frame could easily last a century.")}
                </div>
              </div>
              <div className="relative h-[400px] md:h-[500px] rounded-lg overflow-hidden">
                <Image
                  src={pageData?.globalSection?.section5?.image ? urlForImage(pageData.globalSection.section5.image) : "/images/woodTube.png"}
                  alt="Durable wooden bike frame"
                  fill
                  loader={sanityLoader}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 6: Maintenance */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="relative h-[400px] md:h-[500px] rounded-lg overflow-hidden">
                <Image
                  src={pageData?.globalSection?.section6?.image ? urlForImage(pageData.globalSection.section6.image) : "/images/craftImageHome.jpg"}
                  alt="Frame maintenance"
                  fill
                  loader={sanityLoader}
                  className="object-cover"
                />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 font-neuropol">{pageData?.globalSection?.section6?.title || "Maintenance \n As Easy as It Gets"}</h2>
                <div className="w-16 h-1 bg-accent-color mb-6"></div>
                <div className="mb-4">
                  {renderPortableText(pageData?.globalSection?.section6?.description1, "Here's the best part: WOODALPS frames require no special maintenance! Simply follow standard bike care practices, such as cleaning the derailleurs, periodically replacing the chain, and checking the brake pads.")}
                </div>
                <div>
                  {renderPortableText(pageData?.globalSection?.section6?.description2, "To clean the frame, use water or a pressure washer. Any water that enters the hollow tubes will drain out through specially designed holes. The internal surfaces, also coated with varnish, guarantee that the frame is completely water-resistant.")}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 7: Why Choose WOODALPS */}
        <section className="py-16 md:py-24">
          <div className="container-custom">
            <div className="text-center max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 font-neuropol">{t('design_and_engineering.why_choose_woodalps')}</h2>
              <div className="w-16 h-1 bg-accent-color mx-auto mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold mb-3 font-neuropol">{t('design_and_engineering.why_choose_woodalps_performance')}</h3>
                  <p>{t('design_and_engineering.why_choose_woodalps_performance_text')}</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold mb-3 font-neuropol">{t('design_and_engineering.why_choose_woodalps_aesthetic_appeal')}</h3>
                  <p>{t('design_and_engineering.why_choose_woodalps_aesthetic_appeal_text')}</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold mb-3 font-neuropol">{t('design_and_engineering.why_choose_woodalps_durability')}</h3>
                  <p>{t('design_and_engineering.why_choose_woodalps_durability_text')}</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold mb-3 font-neuropol">{t('design_and_engineering.why_choose_woodalps_eco_friendliness')}</h3>
                  <p>{t('design_and_engineering.why_choose_woodalps_eco_friendliness_text')}</p>
                </div>
              </div>
              <div className="mt-8">
                <p className="text-lg mb-4">
                  {t('design_and_engineering.why_choose_woodalps_join_the_community')}<span className="font-neuropol">WOODALPS</span>{t('design_and_engineering.why_choose_woodalps_join_the_community_text')}
                </p>
                <div className="flex justify-center space-x-4 mt-6">
                  <a
                    href="https://www.facebook.com/woodalps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-all duration-300 shadow-sm"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-6 h-6" />
                  </a>
                  <a
                    href="https://www.instagram.com/woodalps_wooden_bikes/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-pink-600 text-white hover:from-amber-600 hover:to-pink-700 transition-all duration-300 shadow-sm"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

// Define a custom getLayout function to apply the layout at the page level
DesignAndEngineering.getLayout = function getLayout(page) {
  return <Layout transparentHeader={true}>{page}</Layout>;
};

export async function getStaticProps({ preview = false, locale = 'en' }) {
  const pageData = await getDesignEngineeringPageData(preview, locale);
  
  return {
    props: {
      pageData: pageData || null,
      preview,
    },
    revalidate: process.env.VERCEL_ENV === 'preview' ? 1 : 86400, 
    };
} 


